import { observer } from 'mobx-react';
import { FC, FormEvent } from 'react';
import BackNextButtons from 'components/BackNextButtons';
import ContactInformation from 'components/forms/ContactInformation';
import DeliveryAddress from 'components/forms/DeliveryAddress';
import HomeAddress from 'components/forms/HomeAddress';
import LegalResidenceCountry from 'components/forms/LegalResidenceCountry';
import SameResidenceAddr, { SameResidenceAddrFormutil } from 'components/forms/SameResidenceAddr';
import TaxCountry from 'components/forms/TaxCountry';
import TaxNumber from 'components/forms/TaxNumber';
import { usePowerNavigate } from 'components/PowerNavigate';
import Toast from 'components/Toast';
import { Fade } from 'components/Transition';
import { useCheckUSAPostal } from 'hooks/useCheckPostal';
import type { OCRFormutil } from 'modules/Open/type';
import security from 'stores/security';
import values from 'stores/values';
import { reportEventList } from 'utils/gtag';
import { isEmptyValue } from 'utils/helper';
import { setEmail, setPhone } from 'utils/request';
import withProgressForm, { ProgressFormProps } from 'utils/withProgressForm';

// 通讯及地址信息+税务信息
const Address: FC<ProgressFormProps> = ({ onSubmit, $formutil }) => {
    const navigate = usePowerNavigate();
    const { checkPostalMatchStateCodeError } = useCheckUSAPostal();
    const { email, phone, telCode } = security.userInfo;
    const {
        same_delivery_and_residence,
        same_residence_addr: formSameResidenceAddr,
        has_delivery_address,
        verify_code,
        email: formEmail,
        tel_code: formTelCode,
        phone: formPhone
    } = $formutil.$params;
    const {
        legal_residence_country,
        tax_residence_country,
        temporary_is_four_chn,
        issue_country,
        id_type,
        same_residence_addr: storageSameResidenceAddr
    } = values.getParams<OCRFormutil & SameResidenceAddrFormutil>();

    const handleSubmit = (ev: FormEvent) => {
        onSubmit(async () => {
            Toast.loading(true);

            try {
                const deliveryPostalError = await checkPostalMatchStateCodeError(
                    'delivery_postal',
                    'delivery_state_code',
                    'delivery_address_type'
                );

                if (!deliveryPostalError) {
                    reportEventList('contact');

                    if (!email && formEmail && verify_code) {
                        await setEmail({
                            email: formEmail,
                            verify_code
                        });

                        await security.getStatus();
                    }

                    if (!phone && formPhone && verify_code && formTelCode) {
                        await setPhone({
                            phone: formPhone,
                            tel_code: formTelCode,
                            verify_code
                        });

                        await security.getStatus();
                    }

                    if (temporary_is_four_chn) {
                        // 在改变是否有邮寄地址后，因为要去不同流程，所以需要清空差异流程的数据
                        if (has_delivery_address) {
                            values.mergeParams('exemption', {});
                        } else {
                            values.mergeParams('deliveryAddress', {});
                        }

                        navigate(has_delivery_address ? '../delivery-address' : '../exemption');
                    } else {
                        if (
                            !isEmptyValue(storageSameResidenceAddr) &&
                            !isEmptyValue(formSameResidenceAddr) &&
                            storageSameResidenceAddr !== formSameResidenceAddr
                        ) {
                            // 只有same_residence_addr选项发生修改时才需要重置地址证明页的缓存
                            values.mergeParams('residence', {});
                        }

                        // 仅same_residence_addr值为1时，不需要收集地址证明
                        navigate(formSameResidenceAddr === 1 ? '../job' : '../residence');
                    }
                }
            } catch (error) {
                Toast.show(error);
            }

            Toast.loading(false);
        }, ev);
    };

    return (
        <form noValidate onSubmit={handleSubmit}>
            <h3 className="open-title mt-0">{__('CONTACT AND ADDRESS INFORMATION')}</h3>
            <ContactInformation
                emailOptions={{
                    isPreview: !!email,
                    defaultValue: email
                }}
                phoneOptions={{
                    isPreview: !!phone,
                    defaultPhone: phone,
                    defaultTelCode: telCode
                }}
            />
            <h3 className="open-title mt-0">{__('RESIDENTIAL ADDRESS')}</h3>
            <p className="mb-3 normal-text-color">{__("We're legally required to collect this information.")}</p>
            <LegalResidenceCountry disabled $defaultValue={legal_residence_country} isSSN={false} />
            {temporary_is_four_chn ? (
                <HomeAddress
                    isSSN={false}
                    hideMailAddress
                    hasUSDeliveryAddress
                    legalResidenceCountry={legal_residence_country}
                />
            ) : (
                <>
                    <HomeAddress isSSN={false} legalResidenceCountry={legal_residence_country} />
                    <SameResidenceAddr
                        idType={id_type}
                        issueCountry={issue_country}
                        legalResidenceCountry={legal_residence_country}
                    />
                    <Fade in={same_delivery_and_residence === '0'} exit={false}>
                        <DeliveryAddress
                            hasCountry
                            isSSN={false}
                            isFourChn={false}
                            legalResidenceCountry={legal_residence_country}
                        />
                    </Fade>
                    {/* 非SSN非四个中国才收集税号 */}
                    <h3 className="open-title mt-4">{__('TAX INFORMATION')}</h3>
                    <TaxCountry $defaultValue={tax_residence_country} isSSN={false} disabled />
                    <TaxNumber />
                </>
            )}
            <BackNextButtons sticky />
        </form>
    );
};

export default withProgressForm({
    step: 1.5,
    id: 'address',
    basePath: 'open',
    eventParams: {
        exposureEventParams: {
            model: '开户-地址信息',
            name: '地址信息-页面曝光'
        },
        submitEventParams: {
            model: '开户-地址信息',
            name: '地址信息-点击下一步'
        }
    }
})(observer(Address));
